import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Shield, Loader2, Copy, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { CompanyRequire2faToggle } from "@/components/CompanyRequire2faToggle";

export function SettingsTwoFactorSection() {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");

  const load = async () => {
    try {
      const r = await apiClient.get("/2fa/status");
      setStatus(r.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const handleStart = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post("/2fa/setup");
      setSetupData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Setup fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  const handleEnable = async () => {
    if (code.length !== 6) { toast.error("6-stelliger Code erforderlich."); return; }
    setBusy(true);
    try {
      const r = await apiClient.post("/2fa/enable", { code });
      setBackupCodes(r.data.backup_codes);
      setSetupData(null);
      setCode("");
      toast.success("2FA aktiviert. Backup-Codes sicher aufbewahren!");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktivierung fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  const handleDisable = async () => {
    if (!disableCode) { toast.error("Code erforderlich."); return; }
    if (!window.confirm("2FA wirklich deaktivieren?")) return;
    setBusy(true);
    try {
      await apiClient.post("/2fa/disable", { code: disableCode });
      toast.success("2FA deaktiviert.");
      setDisableCode("");
      setBackupCodes(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deaktivierung fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white" id="section-2fa" data-testid="settings-2fa-section">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center flex-shrink-0">
            <Shield className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-slate-900">Zwei-Faktor-Authentifizierung (2FA)</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Schützt deinen Account mit einem 6-stelligen Code aus einer Authenticator-App
              (Google Authenticator, Microsoft Authenticator, 1Password, Authy, etc.).
            </p>
          </div>
        </div>
        {status?.enabled ? (
          <Badge className="bg-emerald-100 text-emerald-800" data-testid="2fa-enabled-badge">✅ aktiv</Badge>
        ) : (
          <Badge className="bg-slate-200 text-slate-700">deaktiviert</Badge>
        )}
      </div>

      {!status?.enabled && !setupData && (
        <Button type="button" size="sm" onClick={handleStart} disabled={busy}
          className="rounded-full bg-slate-900 text-slate-50" data-testid="2fa-start-button">
          {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null} 2FA einrichten
        </Button>
      )}

      {setupData && (
        <div className="rounded-lg border-2 border-indigo-300 bg-indigo-50 p-3 space-y-3" data-testid="2fa-setup-block">
          <div className="text-xs font-semibold text-indigo-900">📱 Schritt 1: QR-Code mit Authenticator-App scannen</div>
          <img src={`data:image/png;base64,${setupData.qr_png_base64}`} alt="2FA QR" className="h-40 w-40 bg-white p-2 rounded" data-testid="2fa-qr-img" />
          <div className="text-[11px] text-slate-600">
            Manuell eintragbarer Schlüssel (falls QR nicht scannbar):
            <code className="ml-1 font-mono bg-white border border-indigo-200 rounded px-1.5 py-0.5" data-testid="2fa-secret">{setupData.secret}</code>
          </div>
          <div className="text-xs font-semibold text-indigo-900 mt-2">🔑 Schritt 2: 6-stelligen Code aus der App eingeben</div>
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="123456" className="font-mono w-32" data-testid="2fa-code-input" />
            <Button type="button" size="sm" onClick={handleEnable} disabled={busy || code.length !== 6}
              className="rounded-full bg-indigo-600 text-white" data-testid="2fa-enable-button">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Aktivieren"}
            </Button>
          </div>
        </div>
      )}

      {backupCodes && (
        <div className="mt-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="2fa-backup-codes">
          <div className="flex items-start gap-2 mb-2">
            <AlertCircle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-xs font-semibold text-amber-900">
              Backup-Codes — JETZT abspeichern! (jeder Code nur 1× verwendbar)
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1 mb-2">
            {backupCodes.map((c) => (
              <code key={c} className="font-mono text-xs bg-white border border-amber-200 rounded px-2 py-1" data-testid={`2fa-backup-${c.replace('-','_')}`}>{c}</code>
            ))}
          </div>
          <Button type="button" size="sm" variant="outline" className="rounded-full"
            onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Alle Codes kopiert."); }}
            data-testid="2fa-backup-copy-all">
            <Copy className="h-3 w-3 mr-1" /> Alle kopieren
          </Button>
        </div>
      )}

      {status?.enabled && !backupCodes && (
        <div className="space-y-2" data-testid="2fa-active-block">
          <div className="text-[11px] text-slate-600">
            Backup-Codes: {status.backup_codes_used} / {status.backup_codes_total} verwendet
            {status.last_verified_at && <> · Letzte Verifikation: {new Date(status.last_verified_at).toLocaleString("de-CH")}</>}
          </div>
          <div className="flex gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-[11px]">Aktueller Code aus App eingeben:</Label>
              <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456" className="font-mono w-32" data-testid="2fa-disable-code-input" />
            </div>
            <Button type="button" size="sm" variant="outline" className="rounded-full text-rose-700 hover:bg-rose-50"
              onClick={handleDisable} disabled={busy || !disableCode}
              data-testid="2fa-disable-button">
              2FA deaktivieren
            </Button>
          </div>
        </div>
      )}
      <CompanyRequire2faToggle />
    </Card>
  );
}
